/**
 * Crisp live chat — the ONE place this site knows anything about the vendor
 * (WWW-S051).
 *
 * Crisp hands you an inline `<script>` to paste into every page. We don't use
 * it, for three reasons that all still hold on a static multi-page site:
 *
 *   * The site's Content-Security-Policy has no `'unsafe-inline'` for scripts
 *     and is not getting one — an inline snippet would simply be refused
 *     (e2e/csp-guard.spec.ts fails the build on one).
 *   * "Every page" means eight HTML documents plus one generated per job
 *     posting. A pasted snippet is a copy of the vendor host in each of them;
 *     this module is imported once by src/main.ts, which every page loads.
 *   * A snippet loads for every visitor whether or not an id is configured,
 *     and it is untestable.
 *
 * The bootstrap below does exactly what the snippet does — set the website id,
 * create the command queue, inject the client — while reading the id from
 * src/config.ts and being safe to call twice.
 *
 * There is no client-side routing here, so the page is read from `location`
 * once at load and never republished; a navigation is a real page load, which
 * boots the widget again. Crisp's own storage is what carries the conversation
 * across that (see docs/support-chat.md).
 */

/** The vendor client. Pinned here so drift shows up in one diff — and it is
 *  the host the CSP has to admit (infra/modules/static-site/main.tf). */
export const CRISP_SCRIPT_SRC = "https://client.crisp.chat/l.js";

/**
 * Segment tagging every conversation opened from this site.
 *
 * The Broker Portal shares this Crisp workspace and tags itself
 * `broker-portal` (BRK-S136), so one inbox holds both and the surface is what
 * tells them apart. Publishing the key is this story's job; the routing rules
 * that act on it are configured Crisp-side, by Aaron, later.
 */
export const SURFACE_SEGMENT = "marketing-site";

/** The coarse area of the site a visitor is on — the second routing key. */
export type PageArea =
  | "home"
  | "about"
  | "team"
  | "contact"
  | "pre-register"
  | "careers"
  | "not-found"
  | "other";

/**
 * First path segment → area. Keyed on the segment rather than the full path so
 * the generated `careers/<slug>/` pages and `pre-register/thanks/` need no
 * entries of their own; the exact path still rides along in the session data.
 */
const AREA_BY_SEGMENT: Record<string, PageArea> = {
  "index.html": "home",
  about: "about",
  team: "team",
  contact: "contact",
  "pre-register": "pre-register",
  careers: "careers",
  "404.html": "not-found",
};

/**
 * Which area of the site a path belongs to. An unregistered path resolves to
 * `"other"` rather than nothing, so a conversation always carries a key — a
 * stale inbound link should still reach a human.
 */
export function pageAreaForPath(pathname: string): PageArea {
  const segments = pathname.split("/").filter(Boolean);
  if (pathname.startsWith("/") && segments.length === 0) return "home";
  return AREA_BY_SEGMENT[segments[0] ?? ""] ?? "other";
}

/**
 * Crisp's command queue. Before `l.js` loads this is a plain array; afterwards
 * it is Crisp's own object. Both answer `push`, which is the entire contract
 * we need — so commands issued on this page load are queued, not dropped.
 */
export interface CrispQueue {
  push(command: unknown[]): void;
}

/**
 * What the loader needs from `window`. Structural rather than `Window` so
 * tests pass a plain object and assert on the commands (no casts to silence
 * the typechecker).
 */
export interface CrispHost {
  $crisp?: CrispQueue;
  CRISP_WEBSITE_ID?: string;
  document: Document;
}

/** Whether live chat is active at all: purely "is a website id configured". */
export function supportChatEnabled(websiteId: string | null): boolean {
  return (websiteId?.trim() ?? "") !== "";
}

/**
 * The segments a Crisp routing rule can key on. The vocabulary is owned HERE
 * so the strings the client publishes and the rules configured in the Crisp
 * dashboard cannot drift apart silently.
 */
export function supportSegments(area: PageArea): string[] {
  return [SURFACE_SEGMENT, area];
}

/**
 * Non-PII context shown beside the conversation in the Crisp inbox. The site
 * is anonymous by definition — there is no sign-in on www — so this is the
 * page a visitor is reading and nothing else.
 */
export function supportSessionData(pathname: string): Record<string, string> {
  return {
    surface: SURFACE_SEGMENT,
    page_area: pageAreaForPath(pathname),
    page: pathname,
  };
}

/** Push a command, or do nothing when the widget was never bootstrapped. */
function push(host: CrispHost, command: unknown[]): void {
  host.$crisp?.push(command);
}

/**
 * Set the website id, create the command queue, and inject the client — once.
 * Returns whether this call did the injecting, so a caller can tell a first
 * bootstrap from a no-op.
 */
export function bootstrapCrisp(host: CrispHost, websiteId: string): boolean {
  host.CRISP_WEBSITE_ID = websiteId;
  // Never replace an existing queue: it may already hold commands, and after
  // l.js loads it is Crisp's own live object.
  host.$crisp ??= [] as unknown[] as CrispQueue;

  if (host.document.querySelector(`script[src="${CRISP_SCRIPT_SRC}"]`)) {
    return false;
  }

  const script = host.document.createElement("script");
  script.src = CRISP_SCRIPT_SRC;
  script.async = true;
  host.document.head.appendChild(script);
  return true;
}

/**
 * Publish the routing key + context onto the current Crisp session.
 *
 * The nesting here is Crisp's, and it is one level deeper than it looks:
 * segments and session data are each passed as a single-element array wrapping
 * the payload. Crisp silently ignores a malformed push — no error, no warning
 * — so a flat array would route nothing and report nothing. Pinned by tests.
 */
export function applySupportContext(host: CrispHost, pathname: string): void {
  push(host, [
    "set",
    "session:segments",
    [supportSegments(pageAreaForPath(pathname))],
  ]);
  push(host, [
    "set",
    "session:data",
    [Object.entries(supportSessionData(pathname))],
  ]);
}

/**
 * Boot live chat for this page load, if it is configured at all. Returns
 * whether the vendor client was injected.
 *
 * Called once from src/main.ts. With no website id this does **nothing**: no
 * `window` property is set, no queue is created, no script is injected and no
 * request reaches Crisp — the site behaves exactly as it did before the
 * feature existed.
 */
export function initSupportChat(
  host: CrispHost,
  pathname: string,
  websiteId: string | null,
): boolean {
  const id = websiteId?.trim() ?? "";
  if (!supportChatEnabled(id)) return false;

  const booted = bootstrapCrisp(host, id);
  applySupportContext(host, pathname);
  return booted;
}
